import { Import, ShieldCheck } from "lucide-react";
import { Link } from "react-router";

import { ExplainedBadge } from "@/shared/components/ExplainedBadge";
import { ingestionRuleIdOf } from "@/shared/domain/ingestionRules";
import { isManaged, type TriggerRecord } from "@/shared/domain/triggers";
import { Goto } from "@/shared/navigation";

import {
  managedByRuleExplanation,
  useIngestionRuleName,
} from "./managedByIngestionRule";
import { systemManagedExplanation } from "./managedTriggerExplanation";
import styles from "./TriggerOwnerBadge.module.css";

/**
 * Who owns a trigger's definition, when it is not the org's own members: the
 * ingestion rule that manages it, or Roboto. Nothing for a trigger the org
 * wrote.
 */
export function TriggerOwnerBadge({ trigger }: { trigger: TriggerRecord }) {
  const ruleId = ingestionRuleIdOf(trigger);
  if (ruleId !== null) {
    return <ManagedByIngestionRuleBadge ruleId={ruleId} />;
  }
  return isManaged(trigger) ? <SystemManagedBadge trigger={trigger} /> : null;
}

/**
 * Marks a trigger an ingestion rule owns with the rule's name. Its popup
 * links to the rule, the only place the trigger is edited.
 */
function ManagedByIngestionRuleBadge({ ruleId }: { ruleId: string }) {
  const ruleName = useIngestionRuleName(ruleId);
  return (
    <ExplainedBadge
      label={
        ruleName === null
          ? "Managed by ingestion rule"
          : `Ingestion rule: ${ruleName}`
      }
      title="Managed by an ingestion rule"
      variant="info"
      icon={<Import className={styles.icon} aria-hidden="true" />}
      explanation={managedByRuleExplanation(ruleName)}
      actions={
        <Link
          to={Goto.createIngestionRuleRoute(ruleId)}
          data-testid="managed-by-ingestion-rule-link"
        >
          Open rule
        </Link>
      }
      data-testid="managed-by-ingestion-rule-badge"
    />
  );
}

/** Marks a trigger Roboto installed and owns the definition of. */
function SystemManagedBadge({ trigger }: { trigger: TriggerRecord }) {
  return (
    <ExplainedBadge
      label="Managed by Roboto"
      variant="info"
      icon={<ShieldCheck className={styles.icon} aria-hidden="true" />}
      explanation={systemManagedExplanation(trigger.managed_by)}
      data-testid="system-managed-badge"
    />
  );
}
